import { useCallback, useEffect, useState } from "react";
import API from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ArrowsClockwise, Globe, Pulse } from "@phosphor-icons/react";
import { StatusTiles } from "../components/system/StatusTiles";
import { StatusCharts } from "../components/system/StatusCharts";
import { EndpointTables } from "../components/system/EndpointTables";
import { RecentErrors } from "../components/system/RecentErrors";
import { DbHealthCard } from "../components/system/DbHealthCard";
import { UptimeTimeline } from "../components/system/UptimeTimeline";
import { TenantTraffic } from "../components/system/TenantTraffic";

const OVERALL = {
  healthy: { label: "All systems normal", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" },
  watch: { label: "Watching a spike", cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" },
  degraded: { label: "Degraded", cls: "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300" },
  critical: { label: "Critical", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" },
};

export default function SystemStatusPage() {
  const [status, setStatus] = useState(null);
  const [range, setRange] = useState("1h");
  const [series, setSeries] = useState(null);
  const [routes, setRoutes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);

  const load = useCallback(async () => {
    try {
      const [{ data: s }, { data: ser }, { data: r }] = await Promise.all([
        API.get("/platform/system-status"), API.get(`/platform/system-status/series?range=${range}`), API.get(`/platform/system-status/routes?range=${range}`),
      ]);
      setStatus(s); setSeries(ser); setRoutes(r);
      setTick((t) => t + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load system status");
    }
  }, [range]);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const runChecks = async () => {
    setBusy(true);
    try {
      await API.post("/platform/system-status/flush-now");
      const { data } = await API.post("/platform/system-status/alerts/run-now");
      toast.success(data.alerted?.length ? `Alert raised: ${data.alerted.join(", ")}` : "Checks run — nothing to alert");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't run checks");
    } finally { setBusy(false); }
  };

  const ov = OVERALL[status?.overall] || OVERALL.healthy;
  return (
    <div className="space-y-6" data-testid="system-status-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2"><Pulse size={22} className="text-blue-600" /> System Status</h1>
          <p className="text-sm text-muted-foreground mt-1">Live resource usage, traffic, error rate and response times of the running app — refreshed every 30 s, {status?.retention_days ?? 7} days of history.</p>
        </div>
        <div className="flex items-center gap-2">
          {status && <Badge className={`text-xs ${ov.cls}`} data-testid="system-overall">{ov.label}</Badge>}
          <Button size="sm" variant="ghost" asChild data-testid="system-public-page-link">
            <a href="/status" target="_blank" rel="noreferrer"><Globe size={14} className="mr-1" /> Public status page</a>
          </Button>
          <Button size="sm" variant="outline" onClick={runChecks} disabled={busy} data-testid="system-run-checks"><ArrowsClockwise size={14} className={`mr-1 ${busy ? "animate-spin" : ""}`} /> Run checks now</Button>
        </div>
      </div>

      {status?.issues?.length > 0 && (
        <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20 p-3 space-y-1" data-testid="system-issues">
          {status.issues.map((i) => (
            <div key={i.key} className="text-xs flex items-center gap-2" data-testid={`system-issue-${i.key}`}>
              <Badge className={`text-[9px] ${i.level === "alert" ? (i.severity === "error" ? "bg-red-100 text-red-700" : "bg-orange-100 text-orange-800") : "bg-amber-100 text-amber-800"}`}>{i.level === "alert" ? "ALERT" : "WATCH"}</Badge>
              <span className="font-medium">{i.label}</span><span className="text-muted-foreground">— {i.detail}</span>
            </div>
          ))}
        </div>
      )}

      <StatusTiles status={status} />

      <div className="flex items-center gap-1" data-testid="system-range">
        {["1h", "6h", "24h", "7d"].map((r) => (
          <Button key={r} size="sm" variant={range === r ? "default" : "outline"} className="h-7 px-3 text-xs" onClick={() => setRange(r)} data-testid={`system-range-${r}`}>{r}</Button>
        ))}
        <span className="text-[11px] text-muted-foreground ml-2">{series?.step_minutes > 1 ? `${series.step_minutes}-minute buckets` : "per minute"}</span>
      </div>

      <StatusCharts series={series} />
      <UptimeTimeline tick={tick} />
      <TenantTraffic range={range} tick={tick} />
      <EndpointTables routes={routes} />
      <div className="grid gap-6 lg:grid-cols-2">
        <RecentErrors />
        <DbHealthCard />
      </div>
    </div>
  );
}
